import React, { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useGetAnnouncementEventsQuery } from "../../../app/api/console/consoleCustomerAnalyticsApiSlice";
import { Card, CardHeader, Donut, EmptyState, EventBadge, Loading, StatCard, formatDuration, formatTime } from "./ui";
import { announcementByRelease, announcementEventsOf, announcementOverTime, announcementSummary } from "./helpers";

// The in-app "What's new in Zenyt" dialog: release_announcement.* events, aggregated
// client-side from the raw event list (the Platform /stats aggregator has no branch for them).
export default function AnnouncementsTab({ timeRange, useProd }) {
  const { data: source = [], isLoading } = useGetAnnouncementEventsQuery({ limit: 1000, timeRange, useProd });
  const events = useMemo(() => announcementEventsOf(source), [source]);
  const summary = useMemo(() => announcementSummary(events), [events]);
  const overTime = useMemo(() => announcementOverTime(events), [events]);
  const byRelease = useMemo(() => announcementByRelease(events), [events]);

  if (isLoading) return <Loading label='Loading announcement events…' />;
  if (events.length === 0) {
    return (
      <EmptyState title={`No release-announcement events in the last ${timeRange}`}>
        Events are sparse — try a wider range. The raw list is capped at 1000 recent events, so older announcements in high-traffic windows may be truncated.
      </EmptyState>
    );
  }

  return (
    <div className='space-y-6' data-testid='tab-announcements'>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5'>
        <StatCard label='Views' value={summary.counts.viewed} note='release_announcement.viewed' labelColor='text-accent-purple' />
        <StatCard label='Dismissals' value={summary.counts.dismissed} note='release_announcement.dismissed' color='text-severity-medium' labelColor='text-severity-medium' />
        <StatCard label='Blog CTR' value={`${summary.blogCtr}%`} note={`${summary.counts.blog_clicked} blog clicks / views`} color='text-accent-green' labelColor='text-accent-green' />
        <StatCard label='Video plays' value={summary.counts.video_played} note={`${summary.videoCtr}% of views`} color='text-pink-600' labelColor='text-pink-600' />
        <StatCard label='Avg open time' value={formatDuration(summary.avgOpenDurationMs)} note={`${summary.reopenRate}% reopened`} />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card className='flex flex-col'>
          <CardHeader
            title='Views vs dismissals over time'
            badge={
              <span className='flex gap-3 font-semibold uppercase tracking-wider'>
                <span className='flex items-center gap-1.5'><span className='w-2.5 h-2.5 rounded-xs bg-[#8B5CF6]' /> Views</span>
                <span className='flex items-center gap-1.5'><span className='w-2.5 h-2.5 rounded-xs bg-[#FF7B43]' /> Dismissals</span>
              </span>
            }
          />
          <div className='h-52'>
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={overTime}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                <XAxis dataKey='date' tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area type='monotone' dataKey='Views' stroke='#8B5CF6' fill='#8B5CF6' opacity={0.6} />
                <Area type='monotone' dataKey='Dismissals' stroke='#FF7B43' fill='#FF7B43' opacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className='flex flex-col'>
          <CardHeader title='How the dialog gets closed' badge={`${summary.counts.dismissed} dismissals`} hint='High CTA share = the primary action resonates; high outside / Esc = users bouncing.' />
          <Donut data={summary.dismissData} total={summary.counts.dismissed} unit='closes' />
        </Card>
      </div>

      <Card>
        <CardHeader title='Engagement by release' badge='grouped by release_version' />
        <div className='border border-border-subtle rounded-xl overflow-hidden'>
          <table className='w-full text-left text-body-xs font-semibold'>
            <thead>
              <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider'>Release</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider'>Views</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider'>Dismissals</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider'>Blog clicks</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider'>Video plays</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider'>Blog CTR</th>
                <th className='px-4 py-2.5 text-right font-bold text-text-secondary uppercase tracking-wider'>Avg open</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-border-subtle'>
              {byRelease.map((r) => (
                <tr key={r.version} className='hover:bg-surface-neutral/30'>
                  <td className='px-4 py-2.5 font-mono text-text-primary'>{r.version}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{r.views}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{r.dismissals}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{r.blogClicks}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-secondary'>{r.videoPlays}</td>
                  <td className='px-4 py-2.5 text-right font-mono text-accent-green font-bold'>{r.blogCtr}%</td>
                  <td className='px-4 py-2.5 text-right font-mono text-text-tertiary'>{formatDuration(r.avgOpenMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <details className='bg-white rounded-2xl border border-border-subtle overflow-hidden group'>
        <summary className='px-5 py-4 list-none flex items-center justify-between cursor-pointer select-none bg-surface-neutral/30 hover:bg-surface-neutral/60'>
          <span className='text-body-sm font-bold text-text-primary'>Raw announcement events <span className='text-caption font-normal text-text-tertiary'>({events.length} rows · for engineers)</span></span>
          <span className='text-caption font-bold text-accent-purple group-open:rotate-180 transition-transform'>▼</span>
        </summary>
        <div className='p-5 overflow-x-auto max-h-96'>
          <table className='w-full text-left text-body-xs font-semibold'>
            <thead>
              <tr className='bg-surface-neutral/60 border-b border-border-subtle'>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-44'>Timestamp</th>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-52'>User</th>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-56'>Event type</th>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider w-28'>Release</th>
                <th className='px-4 py-2.5 font-bold text-text-secondary uppercase tracking-wider'>Properties</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-border-subtle'>
              {events.map((event) => (
                <tr key={event.id} className='hover:bg-surface-neutral/30'>
                  <td className='px-4 py-3 whitespace-nowrap text-text-secondary font-mono text-[11px]'>{formatTime(event.timestamp)}</td>
                  <td className='px-4 py-3 font-mono text-text-primary text-[11px] truncate max-w-[200px]' title={event.user_id}>{event.user_email || event.user_id}</td>
                  <td className='px-4 py-3 whitespace-nowrap'><EventBadge type={event.event_type} /></td>
                  <td className='px-4 py-3 font-mono text-text-secondary text-[10px]'>{(event.properties && event.properties.release_version) || "—"}</td>
                  <td className='px-4 py-3 max-w-xs xl:max-w-md'>
                    <div className='font-mono text-[10px] text-text-secondary truncate' title={event.properties ? JSON.stringify(event.properties) : ""}>
                      {event.properties ? JSON.stringify(event.properties) : "—"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
